import type { WeeklyStatus } from '../../../lib/weeklyApi';

/**
 * Display metadata for each weekly status bucket (CLE-71): the i18n key for the
 * human label, an emoji used in the per-user task list (matching the issue's
 * mockup), and Tailwind classes for the colored status badge / filter chip.
 * Kept in one place so the stats header, filter chips and user cards stay
 * visually consistent. The label itself is resolved at render time via the
 * shared `status.*` translation keys (RO/EN), so this module stays React-free.
 */
export interface WeeklyStatusMeta {
  labelKey: string;
  emoji: string;
  /** Badge classes (background + text) for the colored status chip. */
  badge: string;
  /** Solid accent classes for the active filter chip. */
  activeChip: string;
}

export const WEEKLY_STATUS_META: Record<WeeklyStatus, WeeklyStatusMeta> = {
  DONE: {
    labelKey: 'status.DONE',
    emoji: '✅',
    badge: 'bg-emerald-100 text-emerald-800',
    activeChip: 'bg-emerald-600 text-white',
  },
  IN_PROGRESS: {
    labelKey: 'status.IN_PROGRESS',
    emoji: '🔄',
    badge: 'bg-blue-100 text-blue-800',
    activeChip: 'bg-blue-600 text-white',
  },
  IN_REVIEW: {
    labelKey: 'status.IN_REVIEW',
    emoji: '👁️',
    badge: 'bg-purple-100 text-purple-800',
    activeChip: 'bg-purple-600 text-white',
  },
  QA: {
    labelKey: 'status.QA',
    emoji: '🧪',
    badge: 'bg-teal-100 text-teal-800',
    activeChip: 'bg-teal-600 text-white',
  },
  TODO: {
    labelKey: 'status.TODO',
    emoji: '📋',
    badge: 'bg-amber-100 text-amber-800',
    activeChip: 'bg-amber-500 text-white',
  },
  BLOCKED: {
    labelKey: 'status.BLOCKED',
    emoji: '🚫',
    badge: 'bg-red-100 text-red-800',
    activeChip: 'bg-red-600 text-white',
  },
  BACKLOG: {
    labelKey: 'status.BACKLOG',
    emoji: '🗂️',
    badge: 'bg-gray-100 text-gray-700',
    activeChip: 'bg-gray-600 text-white',
  },
  CANCELLED: {
    labelKey: 'status.CANCELLED',
    emoji: '⛔',
    badge: 'bg-gray-100 text-gray-500',
    activeChip: 'bg-gray-500 text-white',
  },
};

// The statuses exposed in the filter UI, in canonical display order (CLE-71 §6).
// Backlog/Cancelled are intentionally omitted from the primary filter chips —
// they are not part of the requested status toggles — but still render in cards.
export const WEEKLY_FILTER_STATUSES: WeeklyStatus[] = [
  'DONE',
  'IN_PROGRESS',
  'IN_REVIEW',
  'QA',
  'TODO',
  'BLOCKED',
];

// Full canonical order used when rendering a member's status groups.
export const WEEKLY_STATUS_ORDER: WeeklyStatus[] = [
  'DONE',
  'IN_PROGRESS',
  'IN_REVIEW',
  'QA',
  'TODO',
  'BLOCKED',
  'BACKLOG',
  'CANCELLED',
];
